import type { AdminKey } from '@unibyte/i18n/admin';
import { type ReactNode, useEffect, useState } from 'react';
import { NavLink, Navigate, Route, Routes, useLocation, useNavigate } from 'react-router';
import { EmptyState, Icon, Select, ThemeToggle, type IconName, cx } from './components/ui/index.js';
import { LanguageSwitcher, useAdminT } from './lib/admin-locale.js';
import { setTenantId } from './lib/api.js';
import { useRemote } from './lib/remote.js';
import { useSession } from './lib/session.js';
import { useUiPrefs } from './lib/ui-prefs.js';
import { APP_VERSION } from './lib/version.js';
import { ChangePasswordPage, ForgotPasswordPage, TokenPasswordPage } from './pages/AccountPages.js';
import { LoginPage } from './pages/LoginPage.js';
import { OverviewPage } from './pages/OverviewPage.js';
import { BillingPage } from './pages/BillingPage.js';
import { PlansPage, TenantDetailPage, TenantsPage } from './pages/PlatformPages.js';
import {
  AuditPage,
  ConversationsPage,
  LeadsPage,
  MembersPage,
  PlatformPage,
  TranscriptPage,
  UsagePage,
} from './pages/RecordsPages.js';
import { ConversationsInboxPage } from './pages/ConversationsInboxPage.js';
import { KnowledgePage } from './pages/KnowledgePage.js';
import { SiteDetailPage, SitesPage, SourceDetailPage } from './pages/SitesPage.js';
import { ContactsPage } from './pages/ContactsPage.js';
import { OpenAiCostPage } from './pages/OpenAiCostPage.js';
import { PlatformSettingsPage } from './pages/PlatformSettingsPage.js';
import { SettingsPage } from './pages/SettingsPage.js';
import { SetupWizardPage } from './pages/SetupWizardPage.js';
import { SystemStatusPage } from './pages/SystemStatusPage.js';
import {
  SETUP_LATER_KEY,
  readWizardCompleted,
} from './lib/setup-wizard.js';

const AUTH_PATHS = ['/login', '/forgot-password', '/reset-password', '/accept-invite'];
const TENANT_STORAGE_KEY = 'unibyte.admin.tenant';

interface NavItem {
  to: string;
  labelKey: AdminKey;
  icon: IconName;
}

const TENANT_NAV: NavItem[] = [
  { to: '/overview', labelKey: 'nav.overview', icon: 'home' },
  { to: '/sites', labelKey: 'nav.sites', icon: 'globe' },
  { to: '/knowledge', labelKey: 'nav.knowledge', icon: 'file' },
  { to: '/conversations', labelKey: 'nav.conversations', icon: 'chat' },
  { to: '/contacts', labelKey: 'nav.contacts', icon: 'mail' },
  { to: '/usage', labelKey: 'nav.usage', icon: 'chart' },
  { to: '/members', labelKey: 'nav.members', icon: 'users' },
  { to: '/audit', labelKey: 'nav.audit', icon: 'shield' },
  { to: '/settings', labelKey: 'nav.settings', icon: 'settings' },
];

const PLATFORM_NAV: NavItem[] = [
  { to: '/platform', labelKey: 'nav.platform', icon: 'server' },
  { to: '/platform/tenants', labelKey: 'nav.tenants', icon: 'building' },
  { to: '/plans', labelKey: 'nav.plans', icon: 'layers' },
  { to: '/platform/billing', labelKey: 'nav.billing', icon: 'file' },
  { to: '/platform/costs', labelKey: 'nav.costs', icon: 'chart' },
  { to: '/platform/settings', labelKey: 'nav.platformSettings', icon: 'settings' },
];

interface TenantRow {
  id: string;
  name: string;
  status: string;
}

function SideLink(props: NavItem & { label: string }) {
  return (
    <NavLink
      to={props.to}
      end={props.to === '/platform'}
      className={({ isActive }) =>
        cx(
          'admin-nav-link group flex shrink-0 items-center gap-2.5 rounded-[var(--admin-radius-sm)] px-2.5 py-2 text-[13px] font-medium focus:outline-none focus-visible:ring-2 focus-visible:ring-white/30',
          isActive
            ? 'bg-[color:var(--admin-sidebar-active-bg-safe)] text-[color:var(--admin-sidebar-active-text-safe)]'
            : 'text-[color:var(--admin-sidebar-text)] hover:bg-[color:var(--admin-sidebar-hover-bg)] hover:text-[color:var(--admin-sidebar-hover-text)]',
        )
      }
    >
      <Icon
        name={props.icon}
        className="size-3.5 shrink-0 opacity-70 transition-opacity group-hover:opacity-100"
      />
      {props.label}
    </NavLink>
  );
}

function initials(email: string): string {
  return email.slice(0, 2).toUpperCase();
}

type PageCrumb = {
  group: string;
  groupIcon: IconName;
  page: string;
  pageIcon: IconName;
};

/** Top-bar breadcrumb: group + page (with icons). */
function pageCrumbFor(pathname: string, fallback: string, t: (key: AdminKey) => string): PageCrumb {
  if (pathname.startsWith('/system')) {
    return {
      group: t('shell.group.system'),
      groupIcon: 'pulse',
      page: t('nav.systemStatus'),
      pageIcon: 'pulse',
    };
  }
  if (pathname === '/setup') {
    return {
      group: t('shell.group.system'),
      groupIcon: 'pulse',
      page: t('nav.setup'),
      pageIcon: 'settings',
    };
  }

  const platformMatch =
    PLATFORM_NAV.find((item) => item.to === pathname) ??
    PLATFORM_NAV.filter((item) => item.to !== '/' && pathname.startsWith(`${item.to}/`)).sort(
      (a, b) => b.to.length - a.to.length,
    )[0];
  if (platformMatch) {
    return {
      group: t('shell.group.platform'),
      groupIcon: 'server',
      page: t(platformMatch.labelKey),
      pageIcon: platformMatch.icon,
    };
  }
  if (pathname.startsWith('/platform/tenants/')) {
    return {
      group: t('shell.group.platform'),
      groupIcon: 'server',
      page: t('nav.tenants'),
      pageIcon: 'building',
    };
  }

  const tenantMatch =
    TENANT_NAV.find((item) => item.to === pathname) ??
    TENANT_NAV.filter((item) => item.to !== '/' && pathname.startsWith(`${item.to}/`)).sort(
      (a, b) => b.to.length - a.to.length,
    )[0];
  if (tenantMatch) {
    return {
      group: t('shell.group.organization'),
      groupIcon: 'building',
      page: t(tenantMatch.labelKey),
      pageIcon: tenantMatch.icon,
    };
  }
  if (pathname.startsWith('/sites/')) {
    return {
      group: t('shell.group.organization'),
      groupIcon: 'building',
      page: t('nav.sites'),
      pageIcon: 'globe',
    };
  }

  return { group: fallback, groupIcon: 'home', page: fallback, pageIcon: 'home' };
}

function PageTitleBreadcrumb(props: { crumb: PageCrumb }) {
  const { group, groupIcon, page, pageIcon } = props.crumb;
  const same = group === page;
  return (
    <div className="min-w-0">
      <p className="admin-mono flex items-center gap-2 truncate text-[10px] tracking-[0.14em] text-slate-500 uppercase dark:text-slate-400">
        <Icon name={groupIcon} className="size-3 shrink-0" />
        <span className="truncate">{group}</span>
      </p>
      <p className="admin-display mt-0.5 flex min-w-0 items-center gap-2 truncate text-lg font-semibold tracking-[-0.04em] text-slate-950 dark:text-white">
        {!same ? (
          <Icon name={pageIcon} className="size-4 shrink-0 text-[color:var(--admin-accent)]" />
        ) : null}
        <span className="truncate">{page}</span>
      </p>
    </div>
  );
}

function shortAuthLine(raw: string, max = 160): string {
  const trimmed = raw.trim();
  if (!trimmed) return '';
  const sentence = trimmed.split(/(?<=[.!?])\s+/)[0] ?? trimmed;
  return sentence.length > max ? `${sentence.slice(0, max - 1).trim()}…` : sentence;
}

function AuthBrand(props: {
  logoUrl: string;
  productName: string;
  brandMark: string;
  variant: 'panel' | 'bar';
}) {
  if (props.logoUrl) {
    return (
      <img
        src={props.logoUrl}
        alt={props.productName}
        className={
          props.variant === 'panel'
            ? 'relative h-auto max-h-14 w-auto max-w-[14rem] object-contain'
            : 'h-auto max-h-10 w-auto max-w-[11rem] object-contain lg:hidden'
        }
      />
    );
  }

  if (props.variant === 'panel') {
    return (
      <div className="relative flex items-center gap-3">
        <span className="grid size-10 place-items-center rounded-[var(--admin-radius-sm)] border border-white/15 bg-white/10 text-sm font-bold text-white">
          {props.brandMark}
        </span>
        <span className="admin-mono text-[10px] font-medium tracking-[0.2em] text-slate-400 uppercase">
          Signal
        </span>
      </div>
    );
  }

  return (
    <span className="admin-display flex items-center gap-2 text-sm font-semibold text-slate-900 lg:hidden dark:text-slate-100">
      <span className="grid size-8 place-items-center rounded-[var(--admin-radius-sm)] bg-[color:var(--admin-accent,#2563eb)] text-xs font-bold text-white">
        {props.brandMark}
      </span>
      {props.productName}
    </span>
  );
}

function readStoredTenant(): string | null {
  try {
    return window.sessionStorage.getItem(TENANT_STORAGE_KEY);
  } catch {
    return null;
  }
}

export function App() {
  const { user, ready, logout } = useSession();
  const t = useAdminT();
  const { productName, authHeadline, authBody, logoUrl, accentColor } = useUiPrefs();
  const location = useLocation();
  const navigate = useNavigate();
  const [chosenTenant, setChosenTenant] = useState<string | null>(readStoredTenant);
  const platform = user?.platformRole === 'PLATFORM_ADMIN';
  const brandMark = productName.trim().charAt(0).toUpperCase() || 'U';
  const brandBadge = (sizeClass: string, textClass: string) =>
    logoUrl ? (
      <img
        src={logoUrl}
        alt=""
        className={`${sizeClass} rounded-[var(--admin-radius-sm)] object-contain bg-white`}
      />
    ) : (
      <span
        className={`grid ${sizeClass} place-items-center rounded-[var(--admin-radius-sm)] ${textClass}`}
        style={{ backgroundColor: accentColor }}
      >
        {brandMark}
      </span>
    );
  // Platform admins may open any tenant (audited server-side). Everyone else only their memberships.
  const allTenants = useRemote<TenantRow[]>(platform ? '/v1/tenants' : null, null);
  const applicationSettings = useRemote<{ items: Array<{ key: string; value: string }> }>(
    platform ? '/v1/platform/application-settings' : null,
    null,
  );
  const setupCompleted = readWizardCompleted(applicationSettings.data?.items);

  const memberships = user?.memberships ?? [];
  // Clients always land in one of their own tenants. Platform admins start with no tenant selected.
  const membership =
    memberships.find((item) => item.tenantId === chosenTenant) ??
    (platform ? undefined : memberships[0]);
  const supportTenant =
    platform && !membership && chosenTenant
      ? (allTenants.data ?? []).find((item) => item.id === chosenTenant)
      : undefined;
  const tenantId = membership?.tenantId ?? supportTenant?.id ?? null;
  const tenantName = membership?.tenantName ?? supportTenant?.name ?? null;
  const role = membership?.role ?? (supportTenant ? 'PLATFORM_ADMIN' : null);
  const canWrite = membership ? membership.role === 'TENANT_ADMIN' : Boolean(supportTenant);
  setTenantId(tenantId);

  useEffect(() => {
    try {
      if (chosenTenant) window.sessionStorage.setItem(TENANT_STORAGE_KEY, chosenTenant);
      else window.sessionStorage.removeItem(TENANT_STORAGE_KEY);
    } catch {
      // Storage may be unavailable; the selection then lives only in memory.
    }
  }, [chosenTenant]);

  function openTenant(id: string, target = '/overview'): void {
    setChosenTenant(id);
    void navigate(target);
  }

  const home = tenantId ? '/overview' : platform ? '/platform' : '/no-access';
  const guard = (element: ReactNode, allowed = Boolean(user) && Boolean(tenantId)) =>
    allowed ? element : <Navigate to={user ? home : '/login'} replace />;

  const routes = user?.mustChangePassword ? (
    <ChangePasswordPage />
  ) : (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<TokenPasswordPage mode="reset" />} />
      <Route path="/accept-invite" element={<TokenPasswordPage mode="invite" />} />
      <Route path="/" element={<Navigate to={user ? home : '/login'} replace />} />
      <Route path="/overview" element={guard(<OverviewPage canWrite={canWrite} />)} />
      <Route
        path="/sites"
        element={guard(
          <SitesPage
            canWrite={canWrite}
            platform={platform}
            tenantName={tenantName}
            onOpenSite={(id, siteId) => openTenant(id, `/sites/${siteId}`)}
          />,
        )}
      />
      <Route path="/knowledge" element={guard(<KnowledgePage canWrite={canWrite} />)} />
      <Route
        path="/conversations"
        element={guard(<ConversationsInboxPage canWrite={canWrite} />)}
      />
      <Route path="/contacts" element={guard(<ContactsPage canWrite={canWrite} />)} />
      <Route path="/sites/:siteId" element={guard(<SiteDetailPage canWrite={canWrite} />)} />
      <Route
        path="/sites/:siteId/sources/:sourceId"
        element={guard(<SourceDetailPage canWrite={canWrite} />)}
      />
      <Route path="/sites/:siteId/conversations" element={guard(<ConversationsPage />)} />
      <Route
        path="/sites/:siteId/conversations/:conversationId"
        element={guard(<TranscriptPage canWrite={canWrite} />)}
      />
      <Route path="/sites/:siteId/leads" element={guard(<LeadsPage canWrite={canWrite} />)} />
      <Route path="/usage" element={guard(<UsagePage platform={platform} />)} />
      <Route path="/members" element={guard(<MembersPage canWrite={canWrite} />)} />
      <Route path="/audit" element={guard(<AuditPage />)} />
      <Route
        path="/settings"
        element={guard(<SettingsPage canWrite={canWrite} role={role} platform={platform} />)}
      />
      <Route path="/platform/costs" element={guard(<OpenAiCostPage />, platform)} />
      <Route path="/platform/settings" element={guard(<PlatformSettingsPage />, platform)} />
      <Route path="/platform" element={guard(<PlatformPage />, platform)} />
      <Route
        path="/setup"
        element={guard(
          <SetupWizardPage
            onLater={() => {
              try {
                window.sessionStorage.setItem(SETUP_LATER_KEY, '1');
              } catch {
                // The wizard stays available from the menu.
              }
              void navigate('/platform');
            }}
            onFinished={(id) => {
              try {
                window.sessionStorage.removeItem(SETUP_LATER_KEY);
              } catch {
                // Completion is stored on the server.
              }
              if (id) openTenant(id);
              else void navigate('/platform');
            }}
          />,
          platform,
        )}
      />
      <Route path="/platform/tenants" element={guard(<TenantsPage />, platform)} />
      <Route
        path="/platform/tenants/:tenantId"
        element={guard(<TenantDetailPage onOpenAsTenant={openTenant} />, platform)}
      />
      <Route path="/plans" element={guard(<PlansPage />, platform)} />
      <Route path="/platform/billing" element={guard(<BillingPage />, platform)} />
      <Route path="/system" element={guard(<SystemStatusPage />, platform)} />
      <Route
        path="/no-access"
        element={<EmptyState title={t('shell.noAccess.title')} hint={t('shell.noAccess.hint')} />}
      />
      <Route path="*" element={<p className="text-sm text-slate-500">{t('shell.notFound')}</p>} />
    </Routes>
  );

  if (!ready) {
    return (
      <div className="grid min-h-screen place-items-center text-sm text-slate-500">
        {t('shell.loadingSession')}
      </div>
    );
  }

  const authScreen = user?.mustChangePassword || AUTH_PATHS.includes(location.pathname);

  if (authScreen) {
    return (
      <div className="admin-auth-shell min-h-screen lg:grid lg:grid-cols-[minmax(0,1.2fr)_minmax(0,0.95fr)]">
        <aside
          className="admin-auth-panel relative hidden overflow-hidden px-12 py-14 lg:flex lg:flex-col lg:justify-between"
          style={{
            background: 'var(--admin-ink, #0b1220)',
            color: '#94a3b8',
          }}
        >
          <div
            className="pointer-events-none absolute top-0 left-0 h-full w-1"
            style={{ background: 'var(--admin-accent, #2563eb)' }}
          />
          <div
            className="pointer-events-none absolute inset-0 opacity-[0.04]"
            style={{
              backgroundImage:
                'linear-gradient(rgb(255 255 255 / 0.8) 1px, transparent 1px), linear-gradient(90deg, rgb(255 255 255 / 0.8) 1px, transparent 1px)',
              backgroundSize: '56px 56px',
            }}
          />
          <AuthBrand
            logoUrl={logoUrl}
            productName={productName}
            brandMark={brandMark}
            variant="panel"
          />
          <div className="relative max-w-xl">
            <p className="admin-display text-5xl leading-[1.02] font-semibold text-white xl:text-6xl">
              {productName}
            </p>
            <h1 className="mt-8 max-w-md text-lg leading-snug font-medium text-slate-200 xl:text-xl">
              {shortAuthLine(authHeadline, 120)}
            </h1>
            <p className="mt-3 max-w-md text-sm leading-7 text-slate-400">
              {shortAuthLine(authBody, 180)}
            </p>
          </div>
          <p className="admin-mono relative text-[10px] tracking-[0.12em] text-slate-500 uppercase">
            {t('shell.privateAuth')}
            <span className="mt-2 block normal-case tracking-normal">
              {t('shell.version', { version: APP_VERSION })}
            </span>
          </p>
        </aside>
        <main className="relative flex min-h-screen flex-col bg-[color:var(--admin-paper,#f4f6f8)] dark:bg-[color:var(--admin-paper)]">
          <div className="relative flex items-center justify-between gap-3 border-b border-[color:var(--admin-border)] px-6 py-4 sm:px-10 lg:px-12 dark:border-slate-800">
            <AuthBrand
              logoUrl={logoUrl}
              productName={productName}
              brandMark={brandMark}
              variant="bar"
            />
            <div className="ml-auto flex items-center gap-3">
              <LanguageSwitcher />
              <ThemeToggle />
            </div>
          </div>
          <div className="relative flex flex-1 items-center justify-center px-6 pb-16 sm:px-10 lg:px-12">
            {routes}
          </div>
        </main>
      </div>
    );
  }

  if (!user) {
    return (
      <div className="min-h-screen">
        <header className="flex items-center justify-between border-b border-[color:var(--admin-border)] bg-[color:var(--admin-surface)] px-6 py-4 dark:border-[color:var(--admin-border)]">
          <span className="text-sm font-semibold text-slate-900 dark:text-slate-100">
            {productName}
          </span>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <ThemeToggle />
            <NavLink
              to="/login"
              className="text-sm font-medium text-blue-600 hover:text-blue-800 dark:text-blue-400"
            >
              {t('shell.login')}
            </NavLink>
          </div>
        </header>
        <main className="mx-auto max-w-5xl px-6 py-8">{routes}</main>
      </div>
    );
  }

  const tenantOptions: TenantRow[] = platform
    ? (allTenants.data ?? []).map((item) => ({ id: item.id, name: item.name, status: item.status }))
    : memberships.map((item) => ({ id: item.tenantId, name: item.tenantName, status: 'ACTIVE' }));

  return (
    <div className="min-h-screen text-slate-900 md:grid md:grid-cols-[var(--admin-sidebar-width)_1fr] dark:text-slate-100">
      <aside className="relative flex flex-col border-r border-[color:var(--admin-sidebar-border)] bg-[color:var(--admin-sidebar-bg)] text-[color:var(--admin-sidebar-text)] md:sticky md:top-0 md:h-screen md:w-[var(--admin-sidebar-width)] md:overflow-y-auto">
        <div
          className="pointer-events-none absolute top-0 left-0 hidden h-full w-[3px] md:block"
          style={{ background: 'var(--admin-accent, #2563eb)' }}
          aria-hidden
        />
        <div className="flex items-center gap-3 border-b border-[color:var(--admin-sidebar-border)] px-4 py-3.5">
          {brandBadge('size-8', 'text-xs font-bold text-white')}
          <div className="min-w-0">
            <p className="admin-display truncate text-[13px] font-semibold text-[color:var(--admin-sidebar-title)]">
              {productName}
            </p>
            <p className="admin-mono truncate text-[10px] tracking-[0.08em] text-[color:var(--admin-sidebar-muted)] uppercase">
              {platform ? t('shell.platformManage') : t('shell.clientDashboard')}
            </p>
          </div>
        </div>
        <nav
          className="flex flex-1 gap-1 overflow-x-auto px-2.5 py-3 md:flex-col md:gap-5 md:overflow-visible"
          aria-label={t('shell.mainNav')}
        >
          {platform ? (
            <div className="flex gap-1 md:block md:space-y-0.5">
              <p className="admin-section-label hidden px-2.5 pb-1.5 md:block">
                {t('shell.platformSection')}
              </p>
              {PLATFORM_NAV.map((item) => (
                <SideLink key={item.to} {...item} label={t(item.labelKey)} />
              ))}
            </div>
          ) : null}
          {tenantId ? (
            <div className="flex gap-1 md:block md:space-y-0.5">
              <p className="admin-section-label hidden truncate px-2.5 pb-1.5 md:block">
                {platform ? (tenantName ?? t('shell.client')) : t('shell.organization')}
              </p>
              {TENANT_NAV.map((item) => (
                <SideLink key={item.to} {...item} label={t(item.labelKey)} />
              ))}
            </div>
          ) : platform ? (
            <p className="hidden px-2.5 text-xs leading-5 text-[color:var(--admin-sidebar-muted)] md:block">
              {t('shell.pickTenant')}
            </p>
          ) : null}
          {platform ? (
            <div className="flex gap-1 md:block md:space-y-0.5">
              <p className="admin-section-label hidden px-2.5 pb-1.5 md:block">
                {t('shell.systemSection')}
              </p>
              <SideLink
                to="/system"
                labelKey="nav.systemStatus"
                label={t('nav.systemStatus')}
                icon="pulse"
              />
              {setupCompleted !== true ? (
                <SideLink to="/setup" labelKey="nav.setup" label={t('nav.setup')} icon="settings" />
              ) : null}
            </div>
          ) : null}
        </nav>
        <p className="admin-mono px-4 pb-2 text-[10px] tracking-[0.08em] text-[color:var(--admin-sidebar-muted)]">
          {t('shell.version', { version: APP_VERSION })}
        </p>
        <div className="border-t border-[color:var(--admin-sidebar-border)] p-2.5">
          <div className="flex items-center gap-2.5 rounded-[var(--admin-radius-sm)] bg-[color:var(--admin-sidebar-user-bg)] px-2 py-2">
            <span className="admin-mono grid size-7 shrink-0 place-items-center rounded-[var(--admin-radius-sm)] bg-[color:var(--admin-sidebar-user-bg)] text-[10px] font-semibold text-[color:var(--admin-sidebar-user-text)]">
              {initials(user.email)}
            </span>
            <div className="min-w-0 flex-1">
              <p className="truncate text-[12px] font-medium text-[color:var(--admin-sidebar-title)]">
                {user.email}
              </p>
              <p className="admin-mono text-[10px] tracking-[0.06em] text-[color:var(--admin-sidebar-muted)] uppercase">
                {platform
                  ? t('shell.platformAdmin')
                  : canWrite
                    ? t('shell.tenantAdmin')
                    : t('shell.viewer')}
              </p>
            </div>
            <button
              type="button"
              onClick={() => void logout()}
              title={t('shell.logout')}
              aria-label={t('shell.logout')}
              className="rounded-[var(--admin-radius-sm)] p-1.5 text-[color:var(--admin-sidebar-muted)] transition hover:bg-[color:var(--admin-sidebar-hover-bg)] hover:text-[color:var(--admin-sidebar-hover-text)]"
            >
              <Icon name="logout" className="size-3.5" />
            </button>
          </div>
        </div>
      </aside>
      <div className="min-w-0 bg-[color:var(--admin-paper,#f4f6f8)] dark:bg-[color:var(--admin-paper)]">
        <header className="sticky top-0 z-10 flex flex-wrap items-center justify-between gap-3 border-b border-[color:var(--admin-border)] bg-[color:var(--admin-surface)]/92 px-[var(--admin-content-pad-x)] py-2.5 backdrop-blur-sm sm:px-6 xl:px-8">
          <PageTitleBreadcrumb crumb={pageCrumbFor(location.pathname, productName, t)} />
          <div className="flex flex-wrap items-center gap-2">
            <LanguageSwitcher />
            <ThemeToggle />
            {tenantOptions.length > 1 || platform ? (
              <label className="admin-mono flex items-center gap-2 text-[10px] tracking-[0.08em] text-slate-500 uppercase dark:text-slate-400">
                {t('shell.tenantLabel')}
                <Select
                  aria-label={t('shell.tenantLabel')}
                  className="min-w-44 text-sm normal-case tracking-normal"
                  value={tenantId ?? ''}
                  onChange={(next) => {
                    const chosen = next || null;
                    setChosenTenant(chosen);
                    void navigate(chosen ? '/overview' : '/platform');
                  }}
                  options={[
                    ...(platform ? [{ value: '', label: t('shell.noneTenant') }] : []),
                    ...tenantOptions.map((item) => ({
                      value: item.id,
                      label:
                        item.status === 'SUSPENDED'
                          ? `${item.name} (${t('shell.suspended')})`
                          : item.name,
                    })),
                  ]}
                />
              </label>
            ) : null}
          </div>
        </header>
        <main className="w-full px-[var(--admin-content-pad-x)] py-[var(--admin-content-pad-y)] sm:px-6 sm:py-6 xl:px-8">
          {routes}
        </main>
      </div>
    </div>
  );
}
